import { Injectable } from '@angular/core';

// campfire
import { PolicyService, PartnerBrandService } from '@campfire-nx/api';

import { environment } from '@open-portal-env';

import { DOCUMENT_CONSTANTS } from '../constants/policy.constants';

export interface IPolicyDocument {
  type: 'coi' | 'coc' | 'pds' | 'pd' | 'md';
  name: string;
  summary?: string;
  icon?: string;
  linkType?: string;
  id?: string;
}

interface IDocument {
  type: string;
  url?: string;
}

@Injectable({
  providedIn: 'root'
})

export class PolicyDocumentService {
  constructor(
    private policyService: PolicyService,
    private partnerBrandService: PartnerBrandService
  ) { }

  static saveFileClientSide(data: any) {
    const header = data.headers.get('Content-Disposition');
    const fileName = header.split('=')[1] ? header.split('=')[1].replace(/"/g, '') : 'COI';
    const blob = new Blob([data.body], { type: 'application/pdf' });
    const link = document.createElement('a');

    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  }

  downloadDocument(documentType: string, policy: any) {
    switch (documentType) {
      case 'coi':
        if (policy.isRaptorial) {
          this.policyService.downloadCoi(policy.slug)
          .subscribe((data) => {
            PolicyDocumentService.saveFileClientSide(data);
          });
        } else {
          // for legacy policies
          this.policyService.downloadLegacyCoi(policy.productClass, policy.slug)
            .subscribe((data) => {
              PolicyDocumentService.saveFileClientSide(data);
            });
        }
        break;
      case 'coc':
        if (policy.isRaptorial) {
          this.policyService.downloadCoc(policy.slug)
            .subscribe((data) => {
              PolicyDocumentService.saveFileClientSide(data);
            });
        } else {
          // for legacy policies
          this.policyService.downloadLegacyCoc(policy.productClass, policy.slug)
            .subscribe((data) => {
              PolicyDocumentService.saveFileClientSide(data);
            });
        }
        break;
      case 'pds':
      case 'supplementary':
        const document = Object.values(policy.documents).find((key: any) => key.type === documentType) as any;
        window.open(`${environment.apiEndpoint}${document.url}`, '_blank');
        break;
      default:
        break;
    }
  }

  getPolicyDocuments(policy: any) {
    if (!policy) {
      return [];
    }
    const documentList = policy.pds;
    const countryCode = this.partnerBrandService.getCountryCode('country_alpha2') as keyof typeof DOCUMENT_CONSTANTS;
    const productClass = policy.product?.class;
    let policyDocuments = documentList.filter((document: IDocument) => (document.type === 'pds' || document.type === 'supplementary'));

    policyDocuments.push({
      type: 'coi',
      name: DOCUMENT_CONSTANTS[countryCode]?.coi?.title || 'Certificate of Insurance'
    });

    if (productClass === 'car' || productClass === 'home') {
      const cocDocument = {
        type: 'coc',
        name: 'Certificate of Currency'
      }

      policyDocuments = policyDocuments.concat(cocDocument);
    }

    if (productClass === 'travel' && policy.product?.has_medical_declaration && Array.isArray(policy.data?.traveller_list)) {
      const medicalDocuments = this.getMedicalDocumentList(policy.data.traveller_list);
      if (medicalDocuments.length > 0) {
        policyDocuments = policyDocuments.concat(medicalDocuments);
      }
    }

    policyDocuments = this.updateDocumentList(policyDocuments, countryCode);

    return policyDocuments;
  }

  updateDocumentList(documents: Array<IPolicyDocument>, countryCode: keyof typeof DOCUMENT_CONSTANTS) {
      const documentsList = documents;
      const constants = DOCUMENT_CONSTANTS[countryCode];

      if (!constants || Object.keys(constants).length === 0) {
        return [];
      }
      
      documentsList.forEach((document) =>  {
        const docType = document.type;
        if (docType in constants) {
          document.summary = constants[docType as keyof typeof constants].summary;
          document.icon = constants[docType as keyof typeof constants].icon;
          document.linkType = 'download';
        }
      });
      
      return documentsList;
  }

  getMedicalDocumentList(travellerList: any) {
    const documents: IPolicyDocument[] = [];

    travellerList.forEach((traveller: any) => {
      let travellerName = `${traveller.traveller_medical_data_given_name || ''} ${traveller.traveller_medical_data_last_name || ''}`;

      if (traveller.traveller_medical_data_given_name || traveller.traveller_medical_data_last_name) {
        travellerName = `- ${travellerName}`;
      }

      documents.push({
        type: 'md',
        name: `Medical Declaration ${travellerName}`,
        id: traveller.id
      })
    });
    
    return documents;
  }
}
